// Cola IndexedDB para cambios de validación que no pudieron persistirse
// en el primer intento. Cada item lleva:
//   - driveItemId: clave del documento en validacion.json
//   - snapshot: estado nuevo (a aplicar)
//   - previousSnapshot: estado anterior (para rollback definitivo)
//   - retries: número de intentos fallidos
//   - nextRetryAt: ISO timestamp del próximo retry permitido (backoff
//     exponencial: 1s, 2s, 4s, 8s, 16s, 32s, 60s, 60s — cap en 60s)
//   - enqueuedAt
//
// MAX_RETRIES = 8. Tras 8 intentos fallidos consecutivos, el caller
// (validation-store) hace rollback al previousSnapshot y notifica al
// usuario con un toast de error.

import { getDb, STORE_VALIDATION_QUEUE } from './db.js'

export const MAX_RETRIES = 8

/**
 * Calcula el próximo timestamp de reintento con backoff exponencial.
 * retries = 0 → ahora.
 * retries >= 1 → ahora + min(2^(retries-1), 60) segundos.
 */
export function computeNextRetryAt(retries) {
  if (!retries || retries <= 0) return new Date().toISOString()
  const seconds = Math.min(Math.pow(2, retries - 1), 60)
  return new Date(Date.now() + seconds * 1000).toISOString()
}

export async function enqueueChange({ driveItemId, snapshot, previousSnapshot }) {
  const db = await getDb()
  await db.add(STORE_VALIDATION_QUEUE, {
    driveItemId,
    snapshot,
    previousSnapshot: previousSnapshot ?? null,
    retries: 0,
    nextRetryAt: new Date().toISOString(),
    enqueuedAt: new Date().toISOString()
  })
}

export async function getQueuedChanges() {
  const db = await getDb()
  return db.getAll(STORE_VALIDATION_QUEUE)
}

/**
 * Devuelve los items cuya nextRetryAt ya venció.
 */
export async function getReadyChanges() {
  const all = await getQueuedChanges()
  const now = Date.now()
  return all.filter((item) => new Date(item.nextRetryAt || 0).getTime() <= now)
}

export async function updateChange(id, patch) {
  const db = await getDb()
  const tx = db.transaction(STORE_VALIDATION_QUEUE, 'readwrite')
  const existing = await tx.store.get(id)
  if (!existing) {
    await tx.done
    return null
  }
  const updated = { ...existing, ...patch }
  await tx.store.put(updated)
  await tx.done
  return updated
}

export async function removeChange(id) {
  const db = await getDb()
  await db.delete(STORE_VALIDATION_QUEUE, id)
}

export async function clearQueue() {
  const db = await getDb()
  await db.clear(STORE_VALIDATION_QUEUE)
}

export async function pendingCount() {
  const db = await getDb()
  return db.count(STORE_VALIDATION_QUEUE)
}
